/**
 * SetbackMapPanel — Parcel Research → Requirements: "this lot's setbacks come from a City setback
 * map" (2026-10-16). Some cities keep setback maps that set a lot's setbacks differently from the
 * zone standard; on Santiago the map treats the lot's rear as its front. The required numbers stay
 * in the Airtable fields below this panel; this panel records what Airtable has no field for — the
 * map citation, which property line each yard is measured from, an explanation, and a clip of the
 * map. The Pre-Design report prints the zone standard and this lot's deviation side by side.
 * Data: services/parcels.js (Supabase `parcel_setback_maps`, database/307).
 */
import { useEffect, useRef, useState } from 'react'
import { confirmDialog } from './Dialog.jsx'
import {
  getParcelSetbackMap, saveParcelSetbackMap, clearParcelSetbackMap, uploadParcelSetbackClip,
} from '../services/parcels.js'

const YARDS = [
  { key: 'frontLine', label: 'Front yard', field: 'Required Front Yard Setback' },
  { key: 'rearLine', label: 'Rear yard', field: 'Required Rear Yard Setback' },
  { key: 'side1Line', label: 'Side yard 1', field: 'Required Side Yard Setback 1' },
  { key: 'side2Line', label: 'Side yard 2', field: 'Required Side Yard Setback 2' },
]

export default function SetbackMapPanel({ parcelId, parcelFields, memberName }) {
  const [map, setMap] = useState(undefined)   // undefined = loading; null = zone standard; object = map
  const [saving, setSaving] = useState('')
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  useEffect(() => {
    let live = true
    setMap(undefined); setError('')
    getParcelSetbackMap(parcelId).then(m => { if (live) setMap(m) }).catch(() => { if (live) { setMap(null); setError('Could not load the setback map.') } })
    return () => { live = false }
  }, [parcelId])

  async function turnOn() {
    setSaving('mode'); setError('')
    try { setMap(await saveParcelSetbackMap(parcelId, {}, memberName)) } catch { setError('Could not save.') }
    setSaving('')
  }
  async function turnOff() {
    const filled = map && (map.mapCitation || map.clipPath || YARDS.some(y => map[y.key]) || map.explanation)
    if (filled && !(await confirmDialog({ title: 'Follow the zone standard?', message: 'This removes the setback map citation, the lot lines, the explanation and the map clip from this parcel. The required setback numbers stay as they are.', confirmLabel: 'Remove setback map', danger: true }))) return
    setSaving('mode'); setError('')
    try { setMap(await clearParcelSetbackMap(parcelId)) } catch { setError('Could not save.') }
    setSaving('')
  }
  async function save(key, value) {
    if ((map[key] || '') === (value || '')) return
    setSaving(key); setError('')
    try { setMap(await saveParcelSetbackMap(parcelId, { [key]: value }, memberName)) } catch { setError('Could not save.') }
    setSaving('')
  }
  async function upload(file) {
    if (!file) return
    setSaving('clip'); setError('')
    try { setMap(await uploadParcelSetbackClip(parcelId, file, memberName)) } catch (e) { setError(e.message || 'Could not upload the clip.') }
    setSaving('')
  }
  function onPaste(e) {
    const item = [...(e.clipboardData ? e.clipboardData.items : [])].find(i => i.type.startsWith('image/'))
    if (item) { e.preventDefault(); upload(item.getAsFile()) }
  }
  async function removeClip() {
    if (!(await confirmDialog({ title: 'Remove the map clip?', message: 'The report will no longer show the map image.', confirmLabel: 'Remove clip', danger: true }))) return
    save('clipPath', '')
  }

  const on = !!map
  return (
    <div style={S.box}>
      <div style={S.head}>
        <div style={{ flex: 1 }}>
          <div style={S.title}>Setback source</div>
          <div style={S.sub}>Use "City setback map" when the City's map sets this lot's setbacks differently from the zone standard.</div>
        </div>
        <div style={S.segWrap}>
          <button disabled={map === undefined || !!saving} style={{ ...S.seg, ...(!on ? S.segOn : {}) }} onClick={() => on && turnOff()}>Zone standard</button>
          <button disabled={map === undefined || !!saving} style={{ ...S.seg, borderRight: 'none', ...(on ? S.segOn : {}) }} onClick={() => !on && turnOn()}>City setback map</button>
        </div>
      </div>
      {error && <div style={S.error}>{error}</div>}

      {on && (<>
        <Line label="Map name or citation" saving={saving === 'mapCitation'}>
          <input key={parcelId + 'cit'} style={S.input} defaultValue={map.mapCitation}
            placeholder="e.g. City of Newport Beach Setback Map S-10B"
            onBlur={e => save('mapCitation', e.target.value.trim())} />
        </Line>
        <div style={S.groupLabel}>Each yard is measured from</div>
        {YARDS.map(y => {
          const req = parcelFields && parcelFields[y.field]
          return (
            <Line key={y.key} label={<>{y.label}<span style={S.req}>{req != null && req !== '' ? `${req} ft` : 'no number yet'}</span></>} saving={saving === y.key}>
              <input key={parcelId + y.key} style={S.input} defaultValue={map[y.key]}
                placeholder={y.key === 'frontLine' ? 'e.g. the alley property line (north)' : 'e.g. the street property line (south)'}
                onBlur={e => save(y.key, e.target.value.trim())} />
            </Line>
          )
        })}
        <div style={S.hint}>The numbers come from the Required setback fields below. Enter the map's numbers there.</div>
        <Line label="Explanation (optional)" saving={saving === 'explanation'} top>
          <textarea key={parcelId + 'exp'} style={{ ...S.input, minHeight: 64, resize: 'vertical' }} defaultValue={map.explanation}
            placeholder="e.g. The setback map treats the alley side of this lot as its front."
            onBlur={e => save('explanation', e.target.value.trim())} />
        </Line>
        <Line label="Map clip (optional)" saving={saving === 'clip'} top>
          <div style={{ flex: 1, minWidth: 0 }}>
            {map.clipUrl ? (
              <div>
                <img src={map.clipUrl} alt="Setback map clip" style={S.clip} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button style={S.ghostBtn} onClick={() => fileRef.current && fileRef.current.click()}>Replace</button>
                  <button style={S.ghostBtn} onClick={removeClip}>Remove</button>
                </div>
                <input key={parcelId + 'cap'} style={{ ...S.input, width: '100%', marginTop: 8 }} defaultValue={map.clipCaption}
                  placeholder="Caption, e.g. Setback Map S-10B, detail at Santiago Dr."
                  onBlur={e => save('clipCaption', e.target.value.trim())} />
              </div>
            ) : (
              <div tabIndex={0} style={S.drop} onPaste={onPaste}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files && e.dataTransfer.files[0]) }}>
                Click here and paste a screenshot (Ctrl+V), drop an image, or{' '}
                <button style={S.linkBtn} onClick={() => fileRef.current && fileRef.current.click()}>choose a file</button>.
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => { upload(e.target.files && e.target.files[0]); e.target.value = '' }} />
          </div>
        </Line>
      </>)}
    </div>
  )
}

function Line({ label, children, saving, top }) {
  return (
    <div style={{ ...S.row, alignItems: top ? 'flex-start' : 'center' }}>
      <div style={S.rowLabel}>{label}</div>
      <div style={S.rowField}>{children}{saving && <span style={S.savingTag}>saving…</span>}</div>
    </div>
  )
}

const S = {
  box: { maxWidth: 720, border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '12px 14px', marginBottom: 18, background: 'var(--aj-bg-surface)' },
  head: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontSize: 13, fontWeight: 500, color: 'var(--aj-text)' },
  sub: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  groupLabel: { fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', margin: '12px 0 2px' },
  hint: { fontSize: 11, color: 'var(--aj-text-muted)', padding: '6px 0 2px' },
  req: { marginLeft: 8, fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' },
  row: { display: 'flex', gap: 14, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rowLabel: { flex: '0 0 220px', maxWidth: 220, fontSize: 12, color: 'var(--aj-text-mid)', paddingTop: 2 },
  rowField: { flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  segWrap: { display: 'inline-flex', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, overflow: 'hidden', flexShrink: 0 },
  seg: { border: 'none', borderRight: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text-mid)', padding: '6px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif', cursor: 'pointer' },
  segOn: { background: 'var(--aj-accent)', color: 'var(--aj-bg-surface)', fontWeight: 500 },
  drop: { border: '0.5px dashed var(--aj-border-mid)', borderRadius: 4, padding: '18px 12px', fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', outline: 'none' },
  clip: { maxWidth: '100%', maxHeight: 320, border: '0.5px solid var(--aj-border)', borderRadius: 4, display: 'block' },
  ghostBtn: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif' },
  linkBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--aj-accent-blue, #1d6fa5)', fontSize: 12, padding: 0, fontFamily: '"DM Sans",sans-serif', textDecoration: 'underline' },
  savingTag: { fontSize: 10, color: 'var(--aj-text-muted)' },
  error: { color: '#c0392b', fontSize: 12, padding: '6px 0' },
}
